import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  GripVertical,
  Eye,
  EyeOff,
  Loader2,
  HelpCircle,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/faqs`, {
        headers: getAuthHeader()
      });
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/faqs`, formData, {
        headers: getAuthHeader()
      });
      toast.success('FAQ added successfully');
      setShowAddForm(false);
      setFormData({ question: '', answer: '', is_active: true });
      fetchFAQs();
    } catch (error) {
      console.error('Failed to add FAQ:', error);
      toast.error('Failed to add FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/faqs/${id}`, formData, {
        headers: getAuthHeader()
      });
      toast.success('FAQ updated successfully');
      setEditingId(null);
      setFormData({ question: '', answer: '', is_active: true });
      fetchFAQs();
    } catch (error) {
      console.error('Failed to update FAQ:', error);
      toast.error('Failed to update FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/faqs/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('FAQ deleted successfully');
      fetchFAQs();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await axios.put(`${API}/admin/faqs/${faq.id}`, {
        is_active: !faq.is_active
      }, {
        headers: getAuthHeader()
      });
      toast.success(`FAQ ${faq.is_active ? 'hidden' : 'visible'} on website`);
      fetchFAQs();
    } catch (error) {
      console.error('Failed to toggle FAQ:', error);
      toast.error('Failed to update FAQ');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default FAQs. Continue?')) {
      return;
    }

    try {
      await axios.post(`${API}/admin/faqs/seed-defaults`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Default FAQs added successfully');
      fetchFAQs();
    } catch (error) {
      console.error('Failed to seed defaults:', error);
      toast.error(error.response?.data?.detail || 'Failed to add default FAQs');
    }
  };

  const startEdit = (faq) => {
    setEditingId(faq.id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      is_active: faq.is_active
    });
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ question: '', answer: '', is_active: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">FAQ Management</h1>
          <p className="text-slate-400 text-sm mt-1">Manage frequently asked questions displayed on the website</p>
        </div>
        <div className="flex gap-2">
          {faqs.length === 0 && (
            <Button
              onClick={handleSeedDefaults}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Load Defaults
            </Button>
          )}
          <Button
            onClick={() => {
              setShowAddForm(true);
              setEditingId(null);
              setFormData({ question: '', answer: '', is_active: true });
            }}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add FAQ
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-amber-400" />
              Add New FAQ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Question</label>
              <Input
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Enter the question..."
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Answer</label>
              <Textarea
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Enter the answer..."
                rows={4}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <span className="text-sm text-slate-300">Show on website</span>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleAdd}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save FAQ
              </Button>
              <Button
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQs List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-white">
            All FAQs ({faqs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No FAQs yet. Add your first FAQ or load defaults.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className={`p-4 rounded-lg border ${
                    faq.is_active 
                      ? 'bg-slate-900/50 border-slate-700' 
                      : 'bg-slate-900/30 border-slate-700/50 opacity-60'
                  }`}
                >
                  {editingId === faq.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-slate-400 mb-1 block">Question</label>
                        <Input
                          value={formData.question}
                          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                          className="bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-400 mb-1 block">Answer</label>
                        <Textarea
                          value={formData.answer}
                          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                          rows={4}
                          className="bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                        />
                        <span className="text-sm text-slate-300">Show on website</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpdate(faq.id)}
                          disabled={saving}
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        >
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save
                        </Button>
                        <Button
                          onClick={cancelEdit}
                          size="sm"
                          variant="outline"
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="flex items-center gap-2 text-slate-500">
                        <GripVertical className="w-4 h-4" />
                        <span className="text-sm font-mono">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium mb-1">{faq.question}</h4>
                        <p className="text-slate-400 text-sm line-clamp-2">{faq.answer}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(faq)}
                          className={`p-2 rounded-lg transition-colors ${
                            faq.is_active 
                              ? 'text-green-400 hover:bg-green-500/10' 
                              : 'text-slate-500 hover:bg-slate-700'
                          }`}
                          title={faq.is_active ? 'Visible on website' : 'Hidden from website'}
                        >
                          {faq.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => startEdit(faq)}
                          className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-700 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(faq.id)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFAQs;
